import { traerTodas } from '@utils/todasLasPaginas';
import type { ModuloBloqueable, PatchProyectoPayload } from '@erp/shared';
import { apiClient } from './client';
// §28 — el shape de las cuentas se define UNA vez, en packages/shared.
import type { CuentasDelProyecto, VistoBuenoSemana } from '@erp/shared';
import type {
  Proyecto, ProyectoDetalle, FiltrosProyectos, CrearProyectoPayload,
  Unidad, CrearUnidadPayload, EditarUnidadPayload, CargarObjetivosPayload, CargarObjetivosResultado, ImportarUnidadesResult, PreviewImportarResult,
  PartidaPresupuesto, CrearPartidaPayload,

  CrearSuplementoPayload, SuplementoPresupuesto,
  GanttPartida, DocumentoProyecto, CrearDocumentoPayload, BypassDocumentoPayload, AlertaProyecto, CrearAlertaPayload,
  RentabilidadData, RentabilidadSnapshot,
  CotizadorConfig, GuardarConfigCotizadorPayload,
  ProyectoPlantilla, CrearPlantillaPayload, ActualizarPlantillaPayload, TipoPlantilla,
  ParametroProyecto, ActualizarParametrosPayload,
  ContratoProyecto, ResumenFinanciero,
  RitmoVenta, AtencionProyecto,
} from '@/types/proyectos';
import type { PaginatedResponse, ApiResponse } from '@/types';
import type { ConteosPorEstadoProyecto, ResumenPortafolio, ObjetivoVentaApi, TotalesDeUnidades } from '@erp/shared';

const mapProyecto = (p: any): Proyecto => ({ ...p, id: p.id ?? p.proyecto_id });

const CONTEOS_VACIOS: ConteosPorEstadoProyecto = { activo: 0, pausado: 0, entregado: 0, cancelado: 0 };
const PORTAFOLIO_VACIO: ResumenPortafolio = { venta_confirmada: 0, unidades_vendidas: 0, unidades_total: 0, avance_promedio: 0, proyectos_total: 0, proyectos_activos: 0, con_alertas: 0 };

export interface ProyectosListResult extends PaginatedResponse<Proyecto> {
  conteos_por_estado: ConteosPorEstadoProyecto;
  resumen_portafolio: ResumenPortafolio;
}

export const fetchProyectos = (f: FiltrosProyectos): Promise<ProyectosListResult> =>
  apiClient
    .get<PaginatedResponse<any> & { conteos_por_estado?: ConteosPorEstadoProyecto; resumen_portafolio?: ResumenPortafolio }>('/proyectos', { params: f })
    .then(r => ({
      ...r.data,
      data: (r.data.data ?? []).map(mapProyecto),
      conteos_por_estado: r.data.conteos_por_estado ?? CONTEOS_VACIOS,
      resumen_portafolio: r.data.resumen_portafolio ?? PORTAFOLIO_VACIO,
    }));

/** I-228: TODOS los proyectos (páginas de 200, nunca «los primeros 100»). Misma forma que `fetchProyectos`, más `truncada`. */
export const fetchTodosLosProyectos = (f: Omit<FiltrosProyectos, 'page' | 'per_page'> = {}) =>
  traerTodas<Proyecto, ProyectosListResult>((page, per_page) => fetchProyectos({ ...f, page, per_page }));

export const fetchProyecto = (id: number) =>
  apiClient.get<ApiResponse<any>>(`/proyectos/${id}`).then(r => mapProyecto(r.data.data) as ProyectoDetalle);

export const fetchRentabilidad = (proyectoId: number) =>
  apiClient.get<ApiResponse<RentabilidadData>>(`/proyectos/${proyectoId}/rentabilidad`).then(r => r.data.data);

export const recalcularRentabilidad = (proyectoId: number) =>
  apiClient.post<ApiResponse<RentabilidadSnapshot>>(`/proyectos/${proyectoId}/simulacion`).then(r => r.data.data);

// 🔴 CORREGIDO — I-RESUMEN, hallazgo de ABRIR LA PANTALLA (no lo cazó ninguna prueba: las
// pruebas mockean el hook, nunca pasan por `client.ts`). Un vendedor real, en el navegador,
// SÍ veía el toast "Se requiere nivel 2. Tu nivel es 1." al entrar a Resumen — me faltó
// aplicar el mismo escape que ya tienen Dinero/Obra/Cobranza. El bloque de Atención se
// omite en silencio (Tarea 4) aunque la pregunta de fondo (¿debería un vendedor verlo?)
// siga abierta — omitir sin avisar es el comportamiento de HOY, decidido o no el de mañana.
export const fetchAlertas = (proyectoId: number, silenciarCandado = false) =>
  apiClient
    .get<ApiResponse<AlertaProyecto[]>>(
      `/proyectos/${proyectoId}/alertas`,
      silenciarCandado ? { headers: { 'X-Omitir-Toast-Candado-Rol': '1' } } : undefined,
    )
    .then(r => r.data.data);

// I-RESUMEN, bloque Ritmo — candado ROLES_VENTAS (`verificarRol`), un 403 aquí es esperado
// para quien no está en la allowlist de ventas; se omite en silencio (Tarea 4).
export const fetchRitmoVenta = (proyectoId: number, silenciarCandado = false) =>
  apiClient
    .get<ApiResponse<RitmoVenta>>(
      `/proyectos/${proyectoId}/ritmo-venta`,
      silenciarCandado ? { headers: { 'X-Omitir-Toast-Candado-Rol': '1' } } : undefined,
    )
    .then(r => r.data.data);

// W5 (15-oct) — «Lo que pide atención» del Resumen: los renglones ya vienen filtrados por el candado de cada fuente.
export const fetchAtencion = (proyectoId: number) =>
  apiClient.get<ApiResponse<AtencionProyecto>>(`/proyectos/${proyectoId}/atencion`).then(r => r.data.data);

export const crearProyecto = (payload: CrearProyectoPayload) =>
  apiClient.post<ApiResponse<Proyecto>>('/proyectos', payload).then(r => r.data.data);

// BUG-280 — `tasa_comision_default` es la comisión que se SUGIERE en las ventas de este proyecto.
// Mau: «no es fija la comisión, déjala configurable» — 6% con master, 4% sin master, 10% en Tulum.
// Cada venta puede traer la suya y le gana a ésta; `null` la borra.
// I-88 T4 (BUG-1500) — payload con nombre (`PatchProyectoPayload`, @erp/shared): antes tipo
// anónimo duplicado a mano aquí Y en hooks/useProyectos.ts:469.
export const patchProyecto = (id: number, data: PatchProyectoPayload) =>
  apiClient.patch(`/proyectos/${id}`, data).then(r => r.data.data as Proyecto);

// ─── Unidades ────────────────────────────────────────────────────

/**
 * 🔴 LOS DECIMALES DE MySQL LLEGAN COMO TEXTO. Aquí se vuelven números — una sola vez, en
 * la frontera por donde entran al frontend.
 *
 * **MEDIDO (2026-09-05), los tres eslabones:** la columna es `decimal`
 * (`tenant-schema.ts:292`) · Drizzle SIEMPRE la devuelve como texto
 * (`drizzle-orm/mysql-core/columns/decimal.js:25`, `mapFromDriverValue` retorna
 * `String(value)` sin excepción) · la ruta manda la fila cruda (`proyectos.service.ts:446`,
 * `.select()` sin columnas).
 *
 * 🔑 **Esto NO cambia el contrato: hace que la realidad se parezca al contrato que todos ya
 * suponían.** El tipo `Unidad` declara estos campos como `number` desde siempre
 * (`types/proyectos.ts:115-120`) y los 15 consumidores ya programan creyendo eso.
 * `/proyectos/:id/unidades` era la ÚNICA ruta de unidades que mandaba el decimal crudo —
 * broker, shapes del mapa, objetivo-venta y tablero ya convertían del lado del servidor.
 * Esto no crea inconsistencia: la cierra.
 *
 * ⚠️ **Por qué se convierte aquí y no en la pantalla:** el texto no sólo se pinta mal, se
 * SUMA mal. `ProyectoDetalle.tsx:349` hace `reduce((s,u) => s + u.precio_lista, 0)` y con
 * texto eso PEGA los números: `"03000000.002250000.00"`. Se pintaba el precio de la primera
 * unidad y se le llamaba «Venta total del proyecto» — un número creíble, que por eso nadie
 * notó. Convertir sólo en mi pantalla habría dejado esa mentira viva.
 */

/**
 * 🔒 EL CANDADO: **preserva el valor, o dice que no sabe.**
 *
 * Lo que no se pueda convertir sin perder valor sale `null`, **nunca 0**: un `0` afirma
 * «esta unidad cuesta cero» —una afirmación— y `null` dice «no sé», que es la verdad. Es la
 * misma regla que ya rige la ficha, donde no se pinta «—» porque un guion diría «no hay».
 */
export const aNumero = (v: unknown): number | null => {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Los 4 campos `DECIMAL` de `unidades`. El resto de la fila NO se toca. */
const DECIMALES_DE_UNIDAD = [
  'metros_cuadrados', 'precio_lista', 'precio_vigente', 'precio_objetivo',
  'precio_venta_real', 'valor_de_unidad', 'm2_construccion', 'm2_jardin', 'm2_bodega', 'banos',
] as const;

/**
 * Deja la unidad como el tipo `Unidad` promete: con números donde dice número.
 *
 * 🔴 **Sólo convierte. No rellena, no redondea, no defaultea.** Un campo ausente sigue
 * ausente y uno nulo sigue nulo — si esto empezara a inventar ceros sería peor que el
 * defecto que vino a cerrar.
 */
export const normalizarUnidadDeApi = (u: unknown): Unidad => {
  if (u == null || typeof u !== 'object') return u as Unidad;
  const fila = { ...(u as Record<string, unknown>) };
  for (const campo of DECIMALES_DE_UNIDAD) {
    if (campo in fila) fila[campo] = aNumero(fila[campo]);
  }
  return fila as unknown as Unidad;
};

export const fetchUnidades = (proyectoId: number) =>
  apiClient.get<ApiResponse<Unidad[]>>(`/proyectos/${proyectoId}/unidades`)
    .then(r => (r.data.data ?? []).map(normalizarUnidadDeApi));

/** I-222 (T13·1) — el valor del inventario del proyecto completo: lo calcula el servidor, la pantalla lo pinta. */
export const fetchTotalesDeUnidades = (proyectoId: number) =>
  apiClient.get<ApiResponse<TotalesDeUnidades>>(`/proyectos/${proyectoId}/unidades/totales`).then(r => r.data.data);

/** T4 — la meta del proyecto, la brecha y el % sugerido. Solo lectura: no cambia precios. */
export const fetchObjetivoVenta = (proyectoId: number) =>
  apiClient.get<ApiResponse<ObjetivoVentaApi>>(`/proyectos/${proyectoId}/objetivo-venta`).then(r => r.data.data);

export const crearUnidad = ({ proyectoId, payload }: { proyectoId: number; payload: CrearUnidadPayload }) =>
  apiClient.post<ApiResponse<{ id: number }>>(`/proyectos/${proyectoId}/unidades`, payload).then(r => r.data.data);

export const editarUnidad = ({ proyectoId, unidadId, payload }: { proyectoId: number; unidadId: number; payload: EditarUnidadPayload }) =>
  apiClient.patch(`/proyectos/${proyectoId}/unidades/${unidadId}`, payload).then(r => r.data);

/** BUG-4178 (bodega T7·10) — UNA llamada, UNA transacción en el servidor; `solo_vista` calcula sin escribir. */
export const cargarObjetivos = ({ proyectoId, payload }: { proyectoId: number; payload: CargarObjetivosPayload }) =>
  apiClient.post<ApiResponse<CargarObjetivosResultado>>(`/proyectos/${proyectoId}/unidades/objetivos`, payload).then(r => r.data.data);

// I-52 (BUG-1153) — sólo N4, y el servidor exige nombre exacto + motivo ≥30 chars
// (`assertNotaBypass`). `apiClient.delete` no manda cuerpo salvo que se pase en `data`.
export const eliminarUnidad = ({ proyectoId, unidadId, nombre, motivo }: { proyectoId: number; unidadId: number; nombre: string; motivo: string }) =>
  apiClient.delete(`/proyectos/${proyectoId}/unidades/${unidadId}`, { data: { nombre, motivo } }).then(r => r.data);

export const importarUnidadesExcel = ({ proyectoId, archivo }: { proyectoId: number; archivo: File }) => {
  const fd = new FormData();
  fd.append('archivo', archivo);
  return apiClient.post<ApiResponse<ImportarUnidadesResult>>(
    `/proyectos/${proyectoId}/unidades/importar`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ).then(r => r.data.data);
};

export const previewImportarUnidadesExcel = (proyectoId: number, archivo: File): Promise<PreviewImportarResult> => {
  const fd = new FormData();
  fd.append('archivo', archivo);
  return apiClient
    .post(`/proyectos/${proyectoId}/unidades/importar?preview=true`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
    .then(r => r.data.data as PreviewImportarResult);
};

// ─── D-2026-09-09-F — Catálogo de etapas del proyecto ────────────

/**
 * `crearEtapaProyecto` está pensado para llamarse como "asegurar que exista" —
 * quien lo llama (`RejillaCargador` vía `onAsegurarEtapas`) tolera el 409 de una
 * clave repetida como éxito silencioso, no como error: el objetivo es que la
 * etapa EXISTA al terminar, no que esta llamada en particular la haya creado.
 */
export const crearEtapaProyecto = ({ proyectoId, clave }: { proyectoId: number; clave: string }) =>
  apiClient.post<ApiResponse<{ id: number }>>(`/proyectos/${proyectoId}/etapas`, { clave }).then(r => r.data.data);

// ─── Catálogo Presupuesto ─────────────────────────────────────────
export interface PresupuestoResponse {
  partidas: PartidaPresupuesto[];
  resumen: {
    presupuesto_total: number;
    total_presupuestado: number;
    total_ejercido: number;
    total_comprometido?: number;
    total_disponible?: number;
    /** E-15 — lo que pagaron/comprometieron los «cajones» del sistema (operativos, otros, aporte de overhead…): NO es obra y no entra en el ejercido de arriba. */
    otros_gastos?: { ejercido: number; comprometido: number };
    varianza: number;
    presupuesto_cerrado: boolean;
    tipo_cierre: string;
  };
  /** 05-oct (B-1) — el N1 que captura gastos ve el árbol de partidas pero SIN montos (vienen en 0): no es «no hay presupuesto». */
  montos_ocultos?: boolean;
}

// `silenciarCandado`: ver `fetchResumenFinanciero` arriba — mismo escape, mismo default.
export const fetchPresupuesto = (proyectoId: number, silenciarCandado = false) =>
  apiClient
    .get<ApiResponse<PresupuestoResponse>>(`/proyectos/${proyectoId}/presupuesto`, silenciarCandado ? { headers: { 'X-Omitir-Toast-Candado-Rol': '1' } } : undefined)
    .then(r => r.data.data);

export const crearPartida = ({ proyectoId, payload }: { proyectoId: number; payload: CrearPartidaPayload }) =>
  apiClient.post<ApiResponse<{ id: number; sin_desglosar?: { monto: number; pagos: number } }>>(`/proyectos/${proyectoId}/presupuesto`, payload)
    .then(r => ({ ...r.data.data, message: (r.data as { message?: string }).message }));

export const editarPartida = ({ proyectoId, partidaId, payload }: { proyectoId: number; partidaId: number; payload: { nombre?: string; monto_presupuestado?: string; fecha_inicio?: string | null; fecha_fin?: string | null; metodo_pago?: 'transferencia' | 'cheque' | 'efectivo' | null } }) =>
  apiClient.patch(`/proyectos/${proyectoId}/presupuesto/${partidaId}`, payload).then(r => r.data);

export const eliminarPartida = ({ proyectoId, partidaId }: { proyectoId: number; partidaId: number }) =>
  apiClient.delete(`/proyectos/${proyectoId}/presupuesto/${partidaId}`).then(r => r.data);

// I-174 — el servidor (proyectos.routes.ts, PATCH .../presupuesto/cerrar) nunca leyó `req.body`:
// siempre pone `presupuesto_cerrado: true`. Mandarle `{ cerrar }` no hacía nada — sólo engañaba
// a quien leyera este archivo y creyera que el body decidía algo. Se quita: cerrar es cerrar.
export const cerrarPresupuesto = (proyectoId: number) =>
  apiClient.patch(`/proyectos/${proyectoId}/presupuesto/cerrar`).then(r => r.data);

// I-174 — reabrir el presupuesto es OTRO endpoint (`.../presupuesto/abrir`, `authN4rls`), no
// `/cerrar` con un flag. `handleReabrir` en PresupuestoTab.tsx llamaba a `cerrarPresupuesto(false)`
// creyendo que existía una rama para reabrir — el servidor la ignoraba y volvía a cerrar.
export const abrirPresupuesto = (proyectoId: number) =>
  apiClient.patch(`/proyectos/${proyectoId}/presupuesto/abrir`).then(r => r.data);

export const toggleBloquearPartida = ({ proyectoId, partidaId, bloqueada }: { proyectoId: number; partidaId: number; bloqueada: boolean }) =>
  apiClient.patch(`/proyectos/${proyectoId}/presupuesto/${partidaId}/cerrar`, { bloqueada }).then(r => r.data);

// ─── Suplementos ─────────────────────────────────────────────────
export const fetchSuplementos = (proyectoId: number) =>
  apiClient.get<ApiResponse<SuplementoPresupuesto[]>>(`/proyectos/${proyectoId}/presupuesto/suplementos`).then(r => r.data.data);

export const crearSuplemento = ({ proyectoId, payload }: { proyectoId: number; payload: CrearSuplementoPayload }) =>
  apiClient.post<ApiResponse<{ id: number }>>(`/proyectos/${proyectoId}/presupuesto/suplementos`, payload).then(r => r.data.data);

export const autorizarSuplemento = ({ proyectoId, suplementoId, nota }: { proyectoId: number; suplementoId: number; nota?: string }) =>
  apiClient.patch(`/proyectos/${proyectoId}/presupuesto/suplementos/${suplementoId}/autorizar`, { nota }).then(r => r.data);

// ─── Gantt ───────────────────────────────────────────────────────
export const fetchGantt = (proyectoId: number) =>
  apiClient.get<ApiResponse<GanttPartida[]>>(`/proyectos/${proyectoId}/gantt`).then(r => r.data.data);

// ─── Documentos ──────────────────────────────────────────────────
export const fetchDocumentos = (proyectoId: number) =>
  apiClient.get<ApiResponse<DocumentoProyecto[]>>(`/proyectos/${proyectoId}/documentos`).then(r => r.data.data);

export const crearDocumento = ({
  proyectoId,
  payload,
  archivo,
}: { proyectoId: number; payload: CrearDocumentoPayload; archivo: File }) => {
  const fd = new FormData();
  fd.append('archivo', archivo);
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') fd.append(k, String(v));
  });
  return apiClient.post<ApiResponse<{ id: number; archivo_url: string }>>(
    `/proyectos/${proyectoId}/documentos`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ).then(r => r.data.data);
};

export const eliminarDocumento = ({ proyectoId, documentoId }: { proyectoId: number; documentoId: number }) =>
  apiClient.delete(`/proyectos/${proyectoId}/documentos/${documentoId}`).then(r => r.data);

export const bypassDocumento = ({
  proyectoId,
  payload,
}: { proyectoId: number; payload: BypassDocumentoPayload }) =>
  apiClient.post<ApiResponse<{ id: number }>>(
    `/proyectos/${proyectoId}/documentos/bypass`, payload,
  ).then(r => r.data.data);

// ─── Mapa acciones ─────────────────────────────────────────────
export const bloquearMapa = (proyectoId: number) =>
  apiClient.post(`/proyectos/${proyectoId}/mapa/bloquear`).then(r => r.data);

export const desbloquearMapa = (proyectoId: number) =>
  apiClient.post(`/proyectos/${proyectoId}/mapa/desbloquear`).then(r => r.data);

export const resolverAlerta = ({ proyectoId, alertaId, nota_resolucion }: { proyectoId: number; alertaId: number; nota_resolucion: string }) =>
  apiClient.patch(`/proyectos/${proyectoId}/alertas/${alertaId}/resolver`, { nota_resolucion }).then(r => r.data);

export const crearAlerta = ({ proyectoId, payload }: { proyectoId: number; payload: CrearAlertaPayload }) =>
  apiClient.post<ApiResponse<{ alerta_id: number }>>(`/proyectos/${proyectoId}/alertas`, payload).then(r => r.data.data);

// ─── Usuarios asignables (para selectors de alertas, tareas, estimaciones) ─────
// La lista está en @api/configuracion.ts (fetchUsuariosAsignables) — mismo endpoint,
// mejor tipada (RolUsuario/NivelAcceso). Consumir desde @hooks/useConfiguracion.

/** Quiénes trabajan en ESTE proyecto — la vista al revés de `usuarios-asignables` (bloque de Config). */
export interface UsuarioDelProyecto { id: number; nombre: string; rol: string }
export const fetchUsuariosDelProyecto = (proyectoId: number) =>
  apiClient.get<ApiResponse<UsuarioDelProyecto[]>>(`/proyectos/${proyectoId}/usuarios`).then(r => r.data.data);

// ─── Config cotizador ──────────────────────────────────────────
export const fetchConfigCotizador = (proyectoId: number) =>
  apiClient.get<ApiResponse<CotizadorConfig>>(`/proyectos/${proyectoId}/cotizador-config`).then(r => r.data.data);

export const guardarConfigCotizador = ({ proyectoId, payload }: { proyectoId: number; payload: GuardarConfigCotizadorPayload }) =>
  apiClient.put<ApiResponse<{ ok: boolean }>>(`/proyectos/${proyectoId}/cotizador-config`, payload).then(r => r.data.data);

// ─── Plantillas ────────────────────────────────────────────────
export const fetchPlantillas = (proyectoId: number, tipo?: TipoPlantilla) =>
  apiClient.get<ApiResponse<ProyectoPlantilla[]>>(
    `/proyectos/${proyectoId}/plantillas`,
    { params: tipo ? { tipo } : undefined },
  ).then(r => r.data.data);

export const crearPlantilla = ({
  proyectoId,
  payload,
  archivo,
}: { proyectoId: number; payload: CrearPlantillaPayload; archivo?: File }) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined) {
      fd.append(k, Array.isArray(v) ? JSON.stringify(v) : String(v));
    }
  });
  if (archivo) fd.append('archivo', archivo);
  return apiClient.post<ApiResponse<{ id: number }>>(
    `/proyectos/${proyectoId}/plantillas`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ).then(r => r.data.data);
};

export const actualizarPlantillaApi = ({
  proyectoId,
  plantillaId,
  payload,
}: { proyectoId: number; plantillaId: number; payload: ActualizarPlantillaPayload }) =>
  apiClient.put(`/proyectos/${proyectoId}/plantillas/${plantillaId}`, payload).then(r => r.data);

// ─── Cierre de proyecto ────────────────────────────────────────
// I-174 B — `nota` es el bypass de `SALDOS_PENDIENTES_AL_CERRAR` (proyectos.routes.ts,
// `req.body?.nota`, ≥30 chars — `assertNotaBypass`, mismo molde que `autorizarSuplemento`/
// `subirMapaSvg`). Antes esta función no la aceptaba: el CEO veía el detalle de lo que
// faltaba por pagar y no tenía cómo mandar la nota para forzar.
export const cerrarProyecto = (proyectoId: number, modulos?: ModuloBloqueable[], nota?: string) =>
  apiClient.post(`/proyectos/${proyectoId}/cerrar`, {
    ...(modulos && modulos.length > 0 ? { modulos_bloqueados: modulos } : {}),
    ...(nota ? { nota } : {}),
  }).then(r => r.data);

export const abrirProyecto = (proyectoId: number) =>
  apiClient.post(`/proyectos/${proyectoId}/abrir`).then(r => r.data);

// ─── Parámetros de proyecto ────────────────────────────────────
export const fetchParametros = (proyectoId: number) =>
  apiClient.get<ApiResponse<ParametroProyecto | null>>(`/proyectos/${proyectoId}/parametros`).then(r => r.data.data);

export const actualizarParametros = ({ proyectoId, payload }: { proyectoId: number; payload: ActualizarParametrosPayload }) =>
  apiClient.put(`/proyectos/${proyectoId}/parametros`, payload).then(r => r.data);

// ─── Contratos generados (para auto-fill en M09) ──────────────
export const fetchContratosProyecto = (proyectoId: number) =>
  apiClient.get<ApiResponse<ContratoProyecto[]>>(`/proyectos/${proyectoId}/contratos-generados`).then(r => r.data.data);

// ─── Resumen Financiero ────────────────────────────────────────────
// `silenciarCandado`: ver el header `X-Omitir-Toast-Candado-Rol` en `api/client.ts` — para
// un consumidor que sabe de antemano que un 403 aquí es un candado de ROL legítimo (no un
// error), no del nivel. Default false: nadie más cambia de comportamiento.
export const fetchResumenFinanciero = (id: number, silenciarCandado = false): Promise<ResumenFinanciero> =>
  apiClient
    .get(`/proyectos/${id}/resumen`, silenciarCandado ? { headers: { 'X-Omitir-Toast-Candado-Rol': '1' } } : undefined)
    .then(r => r.data.data as ResumenFinanciero);

/** Las cuentas del proyecto con su saldo, comprometido y disponible — una sola llamada. */
export const fetchCuentasDelProyecto = (proyectoId: number) =>
  apiClient.get<ApiResponse<CuentasDelProyecto>>(`/proyectos/${proyectoId}/cuentas`).then(r => r.data.data);

/** Lo pagado esta semana en el proyecto, y si dirección ya lo palomeó. */
export const fetchVistoBueno = (proyectoId: number) =>
  apiClient.get<ApiResponse<VistoBuenoSemana>>(`/proyectos/${proyectoId}/visto-bueno`).then(r => r.data.data);

/** Palomear la semana. No cambia el estado de ningún pago: es un acuse. */
export const palomearSemana = (proyectoId: number) =>
  apiClient.post<{ message: string }>(`/proyectos/${proyectoId}/visto-bueno`, {}).then(r => r.data);

/**
 * BUG-409 — cuánto se le ha entregado YA a cada socio de este proyecto, sumando los
 * dos caminos que Mau decidió mantener por separado (M02 Egresos + M05 Créditos).
 * Consumido por la casa (`PagosDelProyectoTab.tsx`) y por M05
 * (`DistribucionGanancias.tsx`) — es terreno neutral entre los dos.
 */
export interface CruceDistribucionSocio {
  socio_id: number;
  nombre: string;
  via_egresos: number;
  /** Comprometido en Egresos pero AÚN NO pagado (BORRADOR..AUTORIZADO) — el camino de Créditos no tiene esta ventana: nace ya pagado. */
  via_egresos_comprometido: number;
  via_creditos: number;
  total: number;
}

export const fetchCruceDistribucionesSocios = (proyectoId: number) =>
  apiClient.get<ApiResponse<CruceDistribucionSocio[]>>(`/proyectos/${proyectoId}/distribuciones-socios-cruce`)
    .then(r => r.data.data);
